import { forwardRef, ElementType, ReactNode } from "react";

export type Props = {
  /** Element or component to render as */
  as?: ElementType;
  /** Error message text */
  message?: string;
  /** Alternative to message prop for custom content */
  children?: ReactNode;
  /** ID for aria-describedby association with input */
  id?: string;
  /** Additional props passed to the underlying element */
  [key: string]: unknown;
};

/**
 * Base field error component with accessibility attributes.
 * Returns null if neither message nor children is provided.
 *
 * Accessibility:
 * - role="alert" - Immediately announces to screen readers
 * - aria-live="polite" - Waits for user to finish before announcing
 * - Use id prop with aria-describedby on input for association
 */
export const FieldErrorBase = forwardRef<HTMLSpanElement, Props>(
  ({ as = "span", message, children, id, ...props }, ref) => {
    const content = children ?? message;

    if (!content) return null;

    const Component = as as ElementType;

    return (
      <Component
        ref={ref}
        id={id}
        role="alert"
        aria-live="polite"
        {...props}
        {...(id ? { "data-testid": `field-error-${id}` } : {})}
      >
        {content}
      </Component>
    );
  }
);

FieldErrorBase.displayName = "FieldErrorBase";

